/**
 * Phần chia nhân sự **nhìn từ phía doanh thu** — gộp theo kênh, theo người và
 * tổng, kèm luật che theo người đang xem.
 *
 * File này **không có công thức nào**. Nó gọi đúng `gopDoiSoat` của trang Đối
 * soát nhân sự rồi cuộn thêm một cấp (kênh × kỳ → kênh), nên bốn màn hình —
 * trang kênh, module Doanh thu, Tổng quan, danh sách Kênh — không thể lệch số
 * với bản nháp đối soát: chúng đi cùng một đường.
 *
 * Thứ tự tiền (chốt 14/10/2026, không đổi):
 *
 * ```
 * gộp → thuế Mỹ → phần Network → công ty nhận → VAT → THỰC NHẬN
 *     → chi phí vận hành → LỢI NHUẬN KÊNH
 *     → % từng người trong đội ngũ → CÔNG TY GIỮ LẠI
 * ```
 *
 * ## Luật che (quyết định ở máy chủ, không ở giao diện)
 *
 * `nguoiXem.xemHet = false` ⇒ kết quả **không chứa** tên, %, số tiền của người
 * khác, không có `chiaNhanSu`, không có `congTyGiuLai`, không có `theoNguoi`.
 * Che bằng cách **bỏ field** chứ không đặt 0: một số 0 đọc ra là "không ai được
 * chia", và đó là một câu sai.
 *
 * Kênh mà người đang xem **không hưởng %** trả `phanCuaToi = null` — giao diện
 * hiện `—`. `0` ở đó đọc ra là "được chia nhưng ra 0 đồng".
 */
import { z } from 'zod';
import { dongDoiSoatSchema, gopDoiSoat, kenhCoSoDoiSoatSchema, tongUsd, } from './doiSoat.js';
import { vaiTroChiaSchema } from './chiaSe.js';
/* ------------------------------------------------------------------ */
/* DTO                                                                 */
/* ------------------------------------------------------------------ */
/** Một kênh trong kỳ: chuỗi tiền tới lợi nhuận, rồi phần chia (nếu được xem). */
export const kenhChiaNhanSuSchema = kenhCoSoDoiSoatSchema
    .omit({ period: true, soNgayKy: true })
    .extend({
    /** Tổng chia của mọi người trên kênh này. **Chỉ người xem đủ.** */
    chiaNhanSu: z.number().optional(),
    /** Lợi nhuận − tổng chia. Âm khi kênh lỗ mà vẫn chia. **Chỉ người xem đủ.** */
    congTyGiuLai: z.number().optional(),
    /**
     * Dòng chia của kênh. Người xem hẹp **chỉ** có dòng của chính mình; dòng
     * của người khác không đi qua mạng.
     */
    dong: z.array(dongDoiSoatSchema),
    /** `null` = người đang xem không hưởng % ở kênh này ⇒ giao diện hiện `—`. */
    phanCuaToi: z.number().nullable(),
    /**
     * Tỷ lệ của người đang xem ở kênh này. `null` khi họ không có dòng nào, khi
     * tỷ lệ **đổi giữa kỳ** (nhiều mức khác nhau), hoặc khi dòng là khoán cố
     * định — giao diện khi đó chỉ ghi vai trò.
     */
    pctCuaToi: z.number().nullable(),
    vaiTroCuaToi: z.array(vaiTroChiaSchema),
    /**
     * `true` khi còn ít nhất một dòng của kênh này **tính tạm** (kỳ chưa chốt,
     * hoặc đoạn ngày cắt ngang tháng). `false` = mọi dòng lấy từ ảnh chụp lúc
     * chốt, tức số đem đi trả tiền.
     */
    tamTinh: z.boolean(),
});
/** Một người trong kỳ — khối "Chia nhân sự theo người". **Chỉ người xem đủ.** */
export const nguoiChiaNhanSuSchema = z.object({
    userId: z.string(),
    tenNguoi: z.string(),
    avatarColor: z.string(),
    vaiTro: z.array(vaiTroChiaSchema),
    soKenh: z.number().int().nonnegative(),
    phanChia: z.number(),
});
export const tongChiaNhanSuSchema = z.object({
    soKenh: z.number().int().nonnegative(),
    thucNhan: z.number(),
    chiPhiVanHanh: z.number(),
    loiNhuan: z.number(),
    /** Tổng phần của **người đang xem** — số của chính họ, ai cũng được thấy. */
    phanCuaToi: z.number(),
    /** **Chỉ người xem đủ.** */
    soNguoi: z.number().int().nonnegative().optional(),
    chiaNhanSu: z.number().optional(),
    congTyGiuLai: z.number().optional(),
});
export const chiaNhanSuKySchema = z.object({
    kyTu: z.string(),
    kyDen: z.string(),
    /** `true` khi còn đoạn nào tính tạm ⇒ giao diện gắn chip "tạm tính". */
    tamTinh: z.boolean(),
    /** `true` khi người đang xem thấy đủ (xem `lib/chiaDayDu.duocXemChiaDayDu`). */
    xemDayDu: z.boolean(),
    theoKenh: z.array(kenhChiaNhanSuSchema),
    /** **Chỉ người xem đủ** — người xem hẹp không có field này. */
    theoNguoi: z.array(nguoiChiaNhanSuSchema).optional(),
    tong: tongChiaNhanSuSchema,
});
/** Tỷ lệ chung của một tập dòng, `null` khi không chỉ có đúng một mức. */
function pctChung(dong) {
    const mucPct = [...new Set(dong.filter((d) => d.hinhThuc !== 'co_dinh').map((d) => d.pct))];
    return mucPct.length === 1 ? (mucPct[0] ?? null) : null;
}
const HANG_VAI_TRO = { truong_nhom: 0, quan_ly: 1, editor: 2 };
/**
 * Gộp mọi đoạn của một kỳ thành bảng theo kênh + theo người + tổng, rồi che
 * theo người đang xem.
 *
 * Hai cấp cuộn: `gopDoiSoat` cho ra kênh × **đoạn**, hàm này cuộn tiếp về kênh.
 * Mọi phép cộng đi qua `tongUsd` (cent), nên tổng của bảng khớp tuyệt đối với
 * tổng của trang Đối soát chứ không "khớp tới 0,01".
 */
export function gopChiaNhanSu(dau) {
    const { nguoiXem } = dau;
    const tamTinhCuaDoan = new Map(dau.kenh.map((k) => [`${k.channelId}|${k.period}`, k.tamTinh]));
    const gop = gopDoiSoat({
        kenh: dau.kenh,
        dong: dau.dong,
        ...(dau.mauNguoi ? { mauNguoi: dau.mauNguoi } : {}),
    });
    const theoKenhTho = new Map();
    for (const k of gop.theoKenh) {
        const cu = theoKenhTho.get(k.channelId);
        const tamTinh = tamTinhCuaDoan.get(`${k.channelId}|${k.period}`) ?? true;
        if (!cu) {
            theoKenhTho.set(k.channelId, {
                coSo: k,
                thucNhan: [k.thucNhan],
                chiPhi: [k.chiPhiVanHanh],
                loiNhuan: [k.loiNhuan],
                coSoChia: [k.coSoChia],
                phanChia: [k.phanChia],
                congTyGiu: [k.congTyGiu],
                dong: [...k.dong],
                kenhLo: k.kenhLo,
                chiaTheoThucNhan: k.chiaTheoThucNhan,
                tamTinh,
            });
            continue;
        }
        cu.thucNhan.push(k.thucNhan);
        cu.chiPhi.push(k.chiPhiVanHanh);
        cu.loiNhuan.push(k.loiNhuan);
        cu.coSoChia.push(k.coSoChia);
        cu.phanChia.push(k.phanChia);
        cu.congTyGiu.push(k.congTyGiu);
        cu.dong.push(...k.dong);
        /* Kỳ nhiều đoạn: "kênh lỗ" đúng khi **có** đoạn lỗ — nhãn nói về phép chia
           đã dùng, mà một đoạn lỗ là một đoạn đã chia theo thực nhận. */
        cu.kenhLo = cu.kenhLo || k.kenhLo;
        cu.chiaTheoThucNhan = cu.chiaTheoThucNhan || k.chiaTheoThucNhan;
        cu.tamTinh = cu.tamTinh || tamTinh;
    }
    const theoKenh = [...theoKenhTho.values()]
        .map((t) => {
        const cuaToi = t.dong.filter((d) => d.userId === nguoiXem.userId);
        const vaiTroCuaToi = [...new Set(cuaToi.map((d) => d.vaiTro))].sort((a, b) => HANG_VAI_TRO[a] - HANG_VAI_TRO[b]);
        return {
            channelId: t.coSo.channelId,
            tenKenh: t.coSo.tenKenh,
            teamId: t.coSo.teamId,
            tenNhom: t.coSo.tenNhom,
            ngayBatDauVanHanh: t.coSo.ngayBatDauVanHanh,
            chinhSach: t.coSo.chinhSach,
            thucNhan: tongUsd(t.thucNhan),
            chiPhiVanHanh: tongUsd(t.chiPhi),
            loiNhuan: tongUsd(t.loiNhuan),
            coSoChia: tongUsd(t.coSoChia),
            kenhLo: t.kenhLo,
            chiaTheoThucNhan: t.chiaTheoThucNhan,
            tamTinh: t.tamTinh,
            ...(nguoiXem.xemHet
                ? { chiaNhanSu: tongUsd(t.phanChia), congTyGiuLai: tongUsd(t.congTyGiu) }
                : {}),
            dong: nguoiXem.xemHet ? t.dong : cuaToi,
            phanCuaToi: cuaToi.length > 0 ? tongUsd(cuaToi.map((d) => d.phanChia)) : null,
            pctCuaToi: cuaToi.length > 0 ? pctChung(cuaToi) : null,
            vaiTroCuaToi,
        };
    })
        .sort((a, b) => a.tenKenh.localeCompare(b.tenKenh, 'vi'));
    /* ------------------------------- Tổng ----------------------------- */
    const phanCuaToi = tongUsd(dau.dong.filter((d) => d.userId === nguoiXem.userId).map((d) => d.phanChia));
    return {
        kyTu: dau.kyTu,
        kyDen: dau.kyDen,
        tamTinh: theoKenh.some((k) => k.tamTinh),
        xemDayDu: nguoiXem.xemHet,
        theoKenh,
        ...(nguoiXem.xemHet
            ? {
                theoNguoi: gop.theoNguoi.map((n) => ({
                    userId: n.userId,
                    tenNguoi: n.tenNguoi,
                    avatarColor: n.avatarColor,
                    vaiTro: n.vaiTro,
                    soKenh: n.soKenh,
                    phanChia: n.phanChia,
                })),
            }
            : {}),
        tong: {
            soKenh: theoKenh.length,
            thucNhan: gop.tong.thucNhan,
            chiPhiVanHanh: gop.tong.chiPhiVanHanh,
            loiNhuan: gop.tong.loiNhuan,
            phanCuaToi,
            ...(nguoiXem.xemHet
                ? {
                    soNguoi: gop.tong.soNguoi,
                    chiaNhanSu: gop.tong.phanChia,
                    congTyGiuLai: gop.tong.congTyGiu,
                }
                : {}),
        },
    };
}
//# sourceMappingURL=chiaNhanSu.js.map